import './index.css';
import '@tempmee/react/styles.css';
import './themes/tempmee-overrides.css';

import { useEffect, useState } from 'react';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { MotionGlobalConfig } from 'framer-motion';
import 'moment-timezone';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router';
import browserHistory from './browserHistory';

import App from './App';
import './services/runSagas';
import { store } from './store/configureStore';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { growthbook } from './growthbook';
import { CustomSpinner } from './commonComponents';
import { initDatadog } from './services/datadog';
import DatadogErrorBoundary from './components/ErrorBoundary/DatadogErrorBoundary';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

export const queryClient = new QueryClient();

MotionGlobalConfig.skipAnimations = import.meta.env.VITE_TEMP_MEE_ENV === 'test';

// Initialize Datadog RUM and Logging
initDatadog();

const Root = () => {
  const [isReady, setIsReady] = useState(false);

  const init = async () => {
    try {
      await growthbook.init();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`App Init Failed: ${errorMessage}`);
    }
    setIsReady(true);
  };

  useEffect(() => {
    init();
  }, []);

  if (!isReady) {
    return <CustomSpinner isLoading />;
  }

  return (
    <DatadogErrorBoundary>
      <GrowthBookProvider growthbook={growthbook}>
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <Elements stripe={stripePromise}>
              <HistoryRouter basename={import.meta.env.BASE_URL} history={browserHistory as any}>
                <App />
              </HistoryRouter>
            </Elements>
          </QueryClientProvider>
        </Provider>
      </GrowthBookProvider>
    </DatadogErrorBoundary>
  );
};

// Get the root element
const container = document.getElementById('root');

// Create a root
const root = createRoot(container!);
root.render(<Root />);
